// Shared markdown parsing utilities used by both TypewriterMarkdown component and PDF service

// Convert markdown to structured elements with display text and formatting
export const parseMarkdown = (text) => {
  const lines = text.split('\n');
  const elements = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    
    // Empty lines become line breaks
    if (line.trim() === '') {
      if (i < lines.length - 1) { // Only add break if not the last line
        elements.push({ type: 'break', displayText: '\n' });
      }
      continue;
    }

    // Headers (# ## ### etc.)
    if (line.match(/^\s*#{1,6}\s/)) {
      const match = line.match(/^(\s*)(#{1,6})\s(.*)$/);
      if (match) {
        const indent = match[1].length;
        const headerLevel = match[2].length;
        const content = match[3];
        const processedContent = processInlineMarkdown(content);
        elements.push({
          type: 'header',
          level: headerLevel,
          displayText: line,
          elements: processedContent,
          indent: indent,
          content: content
        });
        continue;
      }
    }

    // Bullet points (handle various indentation patterns)
    if (line.match(/^\s*[-*+]\s/)) {
      const match = line.match(/^(\s*)([-*+])\s(.*)$/);
      if (match) {
        const indent = match[1].length;
        const bulletType = match[2];
        const content = match[3];
        const processedContent = processInlineMarkdown(content);
        
        // Calculate nesting level (every 2 spaces = 1 level)
        const nestLevel = Math.floor(indent / 2);
        
        elements.push({ 
          type: 'bullet', 
          displayText: line,
          elements: processedContent,
          indent: indent,
          nestLevel: nestLevel,
          bulletType: bulletType,
          content: content
        });
        continue;
      }
    }

    // Numbered lists
    if (line.match(/^\s*\d+\.\s/)) {
      const match = line.match(/^(\s*)(\d+)\.\s(.*)$/);
      if (match) {
        const indent = match[1].length;
        const number = match[2];
        const content = match[3];
        const processedContent = processInlineMarkdown(content);
        
        // Calculate nesting level
        const nestLevel = Math.floor(indent / 2);
        
        elements.push({ 
          type: 'numbered', 
          number: number,
          displayText: line,
          elements: processedContent,
          indent: indent,
          nestLevel: nestLevel,
          content: content
        });
        continue;
      }
    }

    // Code blocks (``` or indented 4 spaces)
    if (line.match(/^\s*```/) || line.match(/^ {4}/)) {
      elements.push({
        type: 'code',
        displayText: line,
        content: line,
        indent: line.match(/^\s*/)[0].length
      });
      continue;
    }

    // Blockquotes (>)
    if (line.match(/^\s*>\s/)) {
      const match = line.match(/^(\s*)(>+)\s(.*)$/);
      if (match) {
        const indent = match[1].length;
        const quoteLevel = match[2].length;
        const content = match[3];
        const processedContent = processInlineMarkdown(content);
        
        elements.push({
          type: 'blockquote',
          level: quoteLevel,
          displayText: line,
          elements: processedContent,
          indent: indent,
          content: content
        });
        continue;
      }
    }

    // Regular paragraph with inline formatting
    const processedContent = processInlineMarkdown(line);
    elements.push({ 
      type: 'paragraph', 
      displayText: line,
      elements: processedContent,
      indent: line.match(/^\s*/)[0].length,
      content: line
    });
  }

  return elements;
};

// Process inline formatting (bold, italic, code, etc.)
export const processInlineMarkdown = (text) => {
  const elements = [];
  let remaining = text;

  while (remaining.length > 0) {
    // Look for inline code first (`code`)
    const codeMatch = remaining.match(/^(.*?)`([^`]+)`(.*)$/);
    if (codeMatch) {
      if (codeMatch[1]) {
        elements.push({ type: 'text', content: codeMatch[1] });
      }
      elements.push({ type: 'code', content: codeMatch[2] });
      remaining = codeMatch[3];
      continue;
    }

    // Look for bold text (**text** or __text__)
    const boldMatch = remaining.match(/^(.*?)(\*\*|__)(.*?)\2(.*)$/);
    if (boldMatch) {
      if (boldMatch[1]) {
        elements.push({ type: 'text', content: boldMatch[1] });
      }
      elements.push({ type: 'bold', content: boldMatch[3] });
      remaining = boldMatch[4];
      continue;
    }

    // Look for italic text (*text* or _text_)
    const italicMatch = remaining.match(/^(.*?)(\*|_)(.*?)\2(.*)$/);
    if (italicMatch) {
      if (italicMatch[1]) {
        elements.push({ type: 'text', content: italicMatch[1] });
      }
      elements.push({ type: 'italic', content: italicMatch[3] });
      remaining = italicMatch[4];
      continue;
    }

    // Look for links [text](url)
    const linkMatch = remaining.match(/^(.*?)\[([^\]]+)\]\(([^)]+)\)(.*)$/);
    if (linkMatch) {
      if (linkMatch[1]) {
        elements.push({ type: 'text', content: linkMatch[1] });
      }
      elements.push({ 
        type: 'link', 
        content: linkMatch[2], 
        url: linkMatch[3] 
      });
      remaining = linkMatch[4];
      continue;
    }

    // No more formatting, add remaining text
    elements.push({ type: 'text', content: remaining });
    break;
  }

  return elements;
};

// Convert parsed inline elements to plain text for PDF rendering
export const inlineElementsToText = (elements) => {
  return elements.map(element => {
    switch (element.type) {
      case 'bold':
      case 'italic':
      case 'code':
      case 'link':
      case 'text':
      default:
        return element.content;
    }
  }).join('');
};

// Convert parsed markdown elements to structured text for PDF with formatting info
export const elementsToFormattedText = (elements) => {
  const formattedLines = [];
  
  elements.forEach((element) => {
    switch (element.type) {
      case 'header':
        formattedLines.push({
          type: 'header',
          level: element.level,
          text: inlineElementsToText(element.elements),
          indent: element.indent,
          inlineElements: element.elements
        });
        break;
      
      case 'bullet':
        const bulletSymbols = ['•', '◦', '▪', '▫'];
        const bulletSymbol = bulletSymbols[element.nestLevel % bulletSymbols.length];
        formattedLines.push({
          type: 'bullet',
          text: inlineElementsToText(element.elements),
          symbol: bulletSymbol,
          nestLevel: element.nestLevel,
          indent: element.indent,
          inlineElements: element.elements
        });
        break;
      
      case 'numbered':
        formattedLines.push({
          type: 'numbered',
          text: inlineElementsToText(element.elements),
          number: element.number,
          nestLevel: element.nestLevel,
          indent: element.indent,
          inlineElements: element.elements
        });
        break;
      
      case 'code':
        formattedLines.push({
          type: 'code',
          text: element.content,
          indent: element.indent
        });
        break;
      
      case 'blockquote':
        formattedLines.push({
          type: 'blockquote',
          text: inlineElementsToText(element.elements),
          level: element.level,
          indent: element.indent,
          inlineElements: element.elements
        });
        break;
      
      case 'break':
        formattedLines.push({
          type: 'break'
        });
        break;
      
      case 'paragraph':
      default:
        formattedLines.push({
          type: 'paragraph',
          text: inlineElementsToText(element.elements),
          indent: element.indent,
          inlineElements: element.elements
        });
        break;
    }
  });

  return formattedLines;
};